import { useState, useEffect, useMemo, useCallback } from "react";
import { getCategorias, getClientes, getIntermediarios, getSubcategorias, getUsers, supabase } from "../../../utils";
import { fetchUltimasActividadesBulk } from "../../../utils/getCrmActividades";

// [perf] temporal, quitar al cerrar la optimización de Ventas (/admin/ventas) — ver ROADMAP_TECNICO.md
// eslint-disable-next-line no-console
const perfLog = (msg) => console.log(`[perf]${msg}`);

// ─── Umbrales de color (días desde última actividad) ─────────────────────────
// Cliente Top (categoria_cliente A o B) vs Normal (C o sin clasificar)
// Contacto Agencia vs Contacto Cliente tienen exigencia distinta
export const UMBRALES = {
  A: { agencia: { verde: 15, amarillo: 30 }, cliente: { verde: 30, amarillo: 60 } },
  B: { agencia: { verde: 20, amarillo: 35 }, cliente: { verde: 45, amarillo: 75 } },
  C: { agencia: { verde: 30, amarillo: 45 }, cliente: { verde: 60, amarillo: 90 } },
};

const getTier = (categoriaCliente) => (categoriaCliente === "A" || categoriaCliente === "B" ? categoriaCliente : "C");

// ─── Helpers ─────────────────────────────────────────────────────────────────
const PAIS_CODES = { Chile: "CHI", Perú: "PER", Uruguay: "URU" };
const paisToCode = (pais) => PAIS_CODES[pais] || "CHI";

const norm = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

const diasDesde = (fecha) => {
  if (!fecha) return Infinity;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
};

const colorFromDias = (dias, umbral) => {
  if (dias <= umbral.verde) return "verde";
  if (dias <= umbral.amarillo) return "amarillo";
  return "rojo";
};

const COLOR_PRIORIDAD = { rojo: 3, amarillo: 2, verde: 1 };

const peorColor = (colores) => {
  if (colores.length === 0) return "rojo";
  return colores.reduce((peor, c) => (COLOR_PRIORIDAD[c] > COLOR_PRIORIDAD[peor] ? c : peor), "verde");
};

// ─── Subcategorías (por empresa, solo donde aplica) ──────────────────────────
const has = (empresa, kws) => {
  const u = norm(empresa);
  return kws.some((k) => u.includes(norm(k)));
};

const REGIONALES = ["JETSMART", "SKY", "ARAJET", "LATAM", "LAN", "COPA", "AEROLÍNEAS ARGENTINAS"];
const BANCOS_KW = [
  "BCI",
  "BTG",
  "BANCO FALABELLA",
  "CONSORCIO",
  "ITAÚ",
  "LA ARAUCANA",
  "SANTANDER",
  "SCOTIABANK",
  "BICE",
  "BANCO DE CHILE",
  "BANCO ESTADO",
  "BANCO RIPLEY",
];
const APPS_KW = ["UBER", "DIDI", "BEAT", "CABIFY", "RAPPI"];

// Reglas por categoría (clave normalizada sin acentos)
const SUBCATEGORIA_RULES = {
  AEROLINEAS: (e) => (has(e, REGIONALES) ? "Regionales" : "Internacionales"),
  "BANCA & FINANZAS": (e) => {
    if (norm(e).startsWith("AFP")) return "AFP";
    return has(e, BANCOS_KW) ? "Bancos" : "Financieras";
  },
  MOVILIDAD: (e) => (has(e, APPS_KW) ? "Apps" : "Arriendo"),
};

const matchRule = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_RULES).find(([key]) => norm(key) === n)?.[1];
};

// Orden personalizado
const SUBCATEGORIA_ORDER = {
  AEROLINEAS: ["Regionales", "Internacionales"],
  "BANCA & FINANZAS": ["AFP", "Bancos", "Financieras"],
  AUTOMOTRIZ: ["Gama Alta", "Gama Media", "B2B", "Alquiler"],
};

const getSubOrder = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_ORDER).find(([key]) => norm(key) === n)?.[1];
};

// ─── Hook principal ──────────────────────────────────────────────────────────
export const useMapaCalorData = (pais) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  const [categorias, setCategorias] = useState([]);
  const [subcategoriasDB, setSubcategoriasDB] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [actividades, setActividades] = useState({});
  const [contactosAgencia, setContactosAgencia] = useState([]);
  const [contactosCliente, setContactosCliente] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pais) return;
    let cancelled = false;

    const isRefresh = refreshKey > 0;
    const load = async () => {
      if (!isRefresh) setLoading(true);
      try {
        const paisCode = paisToCode(pais);
        // [perf] temporal, quitar al cerrar la optimización de Ventas (ROADMAP_TECNICO.md)
        const perfT0Base = performance.now();
        const [cats, subcats, clis, users, interms, ctosAg, ctosCl] = await Promise.all([
          getCategorias(),
          getSubcategorias(),
          getClientes({ pais }),
          getUsers(),
          getIntermediarios({ pais }).catch(() => []),
          supabase.select("clientes_contactos_agencia").catch(() => []),
          supabase.select("clientes_contactos_cliente").catch(() => []),
        ]);
        // [perf] temporal, quitar al cerrar la optimización de Ventas (ROADMAP_TECNICO.md)
        perfLog(
          `[MapaCalor] ola queries base: ${Math.round(performance.now() - perfT0Base)}ms — ` +
            `clientes ${clis.length}, usuarios ${users.length}, categorias ${cats.length}, ` +
            `subcategorias ${subcats.length}, intermediarios ${(interms || []).length}, ` +
            `ctosAgencia ${(ctosAg || []).length}, ctosCliente ${(ctosCl || []).length}`
        );
        // [perf] temporal
        const perfT0Act = performance.now();
        const acts = await fetchUltimasActividadesBulk(paisCode, clis, users, ctosAg);
        // [perf] temporal
        perfLog(
          "[MapaCalor] ola actividades (fetchUltimasActividadesBulk): " +
            `${Math.round(performance.now() - perfT0Act)}ms — ${Object.keys(acts).length} clientes con actividad`
        );
        if (cancelled) return;
        setCategorias(cats);
        setSubcategoriasDB(subcats);
        setClientes(clis);
        setUsuarios(users.filter((u) => u.habilitado !== false));
        setIntermediarios(interms || []);
        setContactosAgencia(ctosAg || []);
        setContactosCliente(ctosCl || []);
        setActividades(acts);
      } catch {
        // Datos parciales se manejan gracefully
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pais, refreshKey]);

  const usuariosMap = useMemo(() => {
    const map = {};
    usuarios.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [usuarios]);

  const intermediariosMap = useMemo(() => {
    const map = {};
    intermediarios.forEach((i) => {
      map[i.id] = i;
    });
    return map;
  }, [intermediarios]);

  const categoriasMap = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categorias]);

  // Mapea ID de categoría → función de subcategoría (resuelto por ID, no por nombre)
  const subRulesById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const rule = matchRule(c.nombre);
      if (rule) map[c.id] = rule;
    });
    return map;
  }, [categorias]);

  // Mapea subcategoria ID → nombre (desde DB)
  const subcategoriasDBMap = useMemo(() => {
    const map = {};
    subcategoriasDB.forEach((s) => {
      map[s.id] = s.nombre;
    });
    return map;
  }, [subcategoriasDB]);

  // Mapea ID de categoría → orden personalizado de subcategorías
  const subOrderById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const order = getSubOrder(c.nombre);
      if (order) map[c.id] = order;
    });
    return map;
  }, [categorias]);

  // ─── Construir estructura jerárquica ─────────────────────────────────────
  const { data, maxContactos, subcategoriasDisponibles } = useMemo(() => {
    if (clientes.length === 0 || categorias.length === 0) {
      return { data: [], maxContactos: 1, subcategoriasDisponibles: [] };
    }

    const getContactosConCalor = (cli, tipo) => {
      const fields =
        tipo === "agencia"
          ? [cli.contacto_agencia, cli.contacto_agencia_1, cli.contacto_agencia_2, cli.contacto_agencia_3]
          : [cli.contacto_cliente, cli.contacto_cliente_1, cli.contacto_cliente_2, cli.contacto_cliente_3];

      const tier = getTier(cli.categoria_cliente);
      const umbral = UMBRALES[tier][tipo];
      const actPerEmail = actividades[cli.id] || {};

      return fields
        .filter((id) => id != null)
        .map((id) => usuariosMap[id])
        .filter(Boolean)
        .map((user) => {
          const email = user.email?.toLowerCase();
          const lastDate = email ? actPerEmail[email] || null : null;
          const dias = diasDesde(lastDate);
          return { ...user, dias, color: colorFromDias(dias, umbral), lastDate };
        });
    };

    const clientesConCalor = clientes.map((cli) => {
      const contactosAgencia = getContactosConCalor(cli, "agencia");
      const contactosCliente = getContactosConCalor(cli, "cliente");
      const allColors = [...contactosAgencia.map((c) => c.color), ...contactosCliente.map((c) => c.color)];

      const agenciaNombre = cli.agencia ? intermediariosMap[cli.agencia]?.nombre || "Agencia" : "Directo";

      const diasAgencia = contactosAgencia.length > 0 ? Math.min(...contactosAgencia.map((c) => c.dias)) : Infinity;
      const diasCliente = contactosCliente.length > 0 ? Math.min(...contactosCliente.map((c) => c.dias)) : Infinity;

      // Contacto de cliente es obligatorio: si no hay ninguno asignado → rojo automático
      const color = contactosCliente.length === 0 ? "rojo" : peorColor(allColors);

      return {
        ...cli,
        contactosAgencia,
        contactosCliente,
        agenciaNombre,
        diasAgencia,
        diasCliente,
        color,
        categoriaNombre: categoriasMap[cli.categoria]?.nombre || "SIN CATEGORÍA",
      };
    });

    // Agrupar por categoría, luego por marca (empresa) → productos
    const porCategoria = {};
    clientesConCalor.forEach((cli) => {
      const catId = cli.categoria || "sin";
      if (cli.categoriaNombre === "SIN CATEGORÍA") return;

      if (!porCategoria[catId]) {
        porCategoria[catId] = {
          id: catId,
          nombre: cli.categoriaNombre,
          _productos: [],
        };
      }

      porCategoria[catId]._productos.push(cli);
    });

    // Asignar subcategoría, agrupar por marca y construir jerarquía
    const allSubs = new Set();

    Object.values(porCategoria).forEach((cat) => {
      const lista = cat._productos;
      delete cat._productos;

      // Asignar subcategoría: priorizar DB, fallback a reglas hardcodeadas
      lista.forEach((cli) => {
        if (cli.subcategoria_id && subcategoriasDBMap[cli.subcategoria_id]) {
          cli.subcategoria = subcategoriasDBMap[cli.subcategoria_id];
        } else {
          const rule = subRulesById[cli.categoria];
          cli.subcategoria = rule ? rule(cli.empresa) : "General";
        }
        allSubs.add(cli.subcategoria);
      });

      // Agrupar por subcategoría
      const subGroups = {};
      lista.forEach((cli) => {
        if (!subGroups[cli.subcategoria]) subGroups[cli.subcategoria] = [];
        subGroups[cli.subcategoria].push(cli);
      });

      const orderByName = Object.entries(SUBCATEGORIA_ORDER).find(([key]) => norm(key) === norm(cat.nombre))?.[1];
      const customOrder = orderByName || subOrderById[cat.id];

      cat.subcategorias = Object.entries(subGroups)
        .map(([nombre, prods]) => {
          // Dentro de cada subcategoría, agrupar por marca (empresa)
          const marcasMap = {};
          prods.forEach((p) => {
            const marcaKey = (p.empresa || "").toUpperCase().trim();
            if (!marcasMap[marcaKey]) {
              marcasMap[marcaKey] = {
                nombre: p.empresa || "SIN MARCA",
                agenciaNombre: p.agenciaNombre,
                productos: [],
              };
            }
            marcasMap[marcaKey].productos.push(p);
          });

          const marcas = Object.values(marcasMap)
            .map((m) => ({
              ...m,
              productos: m.productos.sort((a, b) => (a.producto || "").localeCompare(b.producto || "")),
              color: peorColor(m.productos.map((p) => p.color)),
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));

          return {
            nombre,
            marcas,
            clientes: prods.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || "")),
            color: peorColor(prods.map((c) => c.color)),
          };
        })
        .sort((a, b) => {
          if (customOrder) {
            const iA = customOrder.indexOf(a.nombre);
            const iB = customOrder.indexOf(b.nombre);
            if (iA !== -1 && iB !== -1) return iA - iB;
            if (iA !== -1) return -1;
            if (iB !== -1) return 1;
          }
          return a.nombre.localeCompare(b.nombre);
        });

      cat.clientes = lista.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || ""));
    });

    // Categorías prioritarias arriba, resto alfabético
    const PRIORIDAD = ["BANCA & FINANZAS", "EDUCACIÓN", "GASTRONOMÍA", "AUTOMOTRIZ", "TECNOLOGÍA"].map(norm);

    const resultado = Object.values(porCategoria)
      .map((cat) => ({
        ...cat,
        color: peorColor(cat.clientes.map((c) => c.color)),
      }))
      .sort((a, b) => {
        const iA = PRIORIDAD.indexOf(norm(a.nombre));
        const iB = PRIORIDAD.indexOf(norm(b.nombre));
        if (iA !== -1 && iB !== -1) return iA - iB;
        if (iA !== -1) return -1;
        if (iB !== -1) return 1;
        return a.nombre.localeCompare(b.nombre);
      });

    return {
      data: resultado,
      maxContactos: 5,
      subcategoriasDisponibles: [...allSubs].sort(),
    };
  }, [
    clientes,
    categorias,
    actividades,
    usuariosMap,
    categoriasMap,
    intermediariosMap,
    subRulesById,
    subOrderById,
    subcategoriasDBMap,
  ]);

  return {
    data,
    loading,
    categorias,
    maxContactos,
    subcategoriasDisponibles,
    // Datos crudos para reutilizar en otros hooks (ej: mapa agencias)
    _raw: { clientes, usuarios, intermediarios, actividades, contactosAgencia, contactosCliente, subcategoriasDB },
    refetch,
  };
};
